<template>
	<EditorShell>
		<!-- Header -->
		<CLayoutEditorHeader
			:isLoading="isLoading"
			:headline="template ? template.name : null"
			:saveButton="$t('templates.save')"
			@back:click="$router.back()"
			@save:click="updateTemplate"
		>
			<!-- <EditorSaveChangesPrompt :isVisible="hasChanged" slot="middle" /> -->
		</CLayoutEditorHeader>

		<!-- Wrapper -->
		<JBox class="s-mx-auto s-p-6">
			<!-- <pre>{{ template }}</pre> -->
			<JBox class="s-mb-12" v-if="template && template.dynamicValues && template.dynamicValues.length">
				<CCodeEditor language="json" :value="table" @input="handleUpdateTable" height="100vh" />
			</JBox>

			<JBox
				v-if="(!template || !template.dynamicValues || !template.dynamicValues.length) && !isLoading"
				class="s-bg-white s-p-8 s-rounded s-shadow s-mb-8"
			>
				<JHeadline class="s-text-lg s-text-gray-700">Let's create some data</JHeadline>
				<JText class="s-text-gray-500">Here you can create arbitrary JSON data</JText>
				<JButton @click="createTable" class="s-mt-8">Create JSON</JButton>
			</JBox>

			<!-- Document Settings -->
			<EditorAccordion :headline="$t('templates.document_settings')" class="s-mb-4">
				<EditorDocumentSettings v-model="template" />
			</EditorAccordion>

			<!-- Delete template -->
			<CDialogProvider
				:headline="$t('templates.delete_template')"
				:description="$t('templates.delete_dialog')"
				:cancelButton="$t('templates.continueEditing')"
				:confirmButton="$t('templates.delete_template')"
				@confirm="deleteTemplate"
			>
				<JFlex class="s-justify-end" slot-scope="{ openDialog }">
					<JButton variant="error" @click="openDialog">{{ $t("templates.delete_template") }} </JButton>
				</JFlex>
			</CDialogProvider>
		</JBox>

		<!-- Debug Console -->
		<EditorDebugConsole :errors="errors" />
	</EditorShell>
</template>

<script>
import EditorShell from "../components/EditorShell"
import EditorAccordion from "../components/EditorAccordion"
// import EditorSaveChangesPrompt from "../components/EditorSaveChangesPrompt"
import EditorDocumentSettings from "../components/EditorDocumentSettings"
import EditorDebugConsole from "../components/EditorDebugConsole"
import { preventBrowserBackButton } from "../utils/preventBrowserBackButton"
import isEqual from "lodash.isequal"
import { deepClone } from "@/utils/"
import api from "@/api/api"
import { query, mutation } from "../api/queries"

export default {
	components: { EditorShell, EditorAccordion, EditorDebugConsole, EditorDocumentSettings },
	props: {
		id: {
			type: String
		}
	},
	data: () => ({
		isLoading: false,

		selectedId: null,
		valueJson: null,

		template: {},
		originalTemplate: {},

		errors: []
	}),

	computed: {
		hasChanged() {
			return !isEqual(this.template, this.originalTemplate)
		},
		computedList() {
			return this.template?.dynamicValues
		},
		computedListTotal() {
			return this.template?.dynamicValues?.length || 0
		},
		computedSelectedValue() {
			return this.template?.dynamicValues?.find(item => item.valueId === this.selectedId)
		},

		table() {
			return this.template?.dynamicValues?.[0]?.valueJsonString ?? ""
		}
	},

	methods: {
		onSelectValueId(valueId) {
			this.selectedId = valueId
		},

		async fetchTemplate() {
			if (this.isLoading) return

			this.setErrors
			this.isLoading = true
			const res = await api({
				query: query.fetchDynamicTemplate,
				variables: {
					where: {
						id: this.id ? this.id : null
					}
				}
			})
			this.isLoading = false

			if (!res?.data?.templates?.list?.[0]) {
				this.setErrors(res?.errors?.map(item => item?.message) | [this.$t("templates.noTemplate")])
				return
			}

			this.template = res?.data?.templates?.list?.[0] ?? {}
			this.originalTemplate = deepClone(res?.data?.templates?.list?.[0] ?? {})
		},

		async deleteTemplate() {
			if (this.isLoading) return
			this.setErrors()

			this.isLoading = true
			const res = await api({
				query: mutation.deleteTemplate,
				variables: { templateId: this.id }
			})
			this.isLoading = false

			if (res.data?.deleteTemplate?.status !== "success") {
				this.setErrors(res?.data?.deleteTemplate?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.$router.push({ name: "ViewTemplatesHome" })
		},

		async updateTemplate() {
			if (this.isLoading) return
			this.setErrors()

			const res = await api({
				query: mutation.updateDynamicTemplate,
				variables: {
					templateId: this.id ? this.id : null,
					input: {
						name: this.template?.name,
						description: this.template?.description,
						logicalId: this.template?.logicalId
					}
				}
			})

			if (res?.data?.updateDynamicTemplate?.status !== "success") {
				this.setErrors(res?.data?.updateDynamicTemplate?.errors || res?.errors?.map(item => item?.message))
				return
			}

			const res2 = await api({
				query: mutation.updateDynamicValueOfTemplate,
				variables: {
					templateId: this.id ? this.id : null,
					valueId: this.template?.dynamicValues?.[0]?.valueId,
					input: { valueJsonString: this.template?.dynamicValues?.[0]?.valueJsonString }
				}
			})
			this.isLoading = false

			if (res2?.data?.updateDynamicValueOfTemplate?.status !== "success") {
				this.setErrors(res?.data?.updateDynamicValueOfTemplate?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.originalTemplate = deepClone(this.template)
			this.fetchTemplate()
		},

		async addDynamicValue({ valueJsonString = "{}" } = {}) {
			if (this.isLoading) return
			this.setErrors()

			this.isLoading = true
			const res = await api({
				query: mutation.addDynamicValueToTemplate,
				variables: {
					templateId: this.id ? this.id : null,
					input: {
						valueJsonString
					}
				}
			})
			this.isLoading = false

			if (res?.data?.addDynamicValueToTemplate?.status !== "success") {
				this.setErrors(res?.data?.addDynamicValueToTemplate?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.fetchTemplate()
		},

		async deleteDynamicValue(valueId) {
			if (this.isLoading) return
			this.setErrors()

			this.isLoading = true
			const res = await api({
				query: mutation.removeDynamicValue,
				variables: {
					templateId: this.id ? this.id : null,
					valueId
				}
			})
			this.isLoading = false

			if (res?.data?.removeDynamicValueFromTemplate?.status !== "success") {
				this.setErrors(res?.data?.removeDynamicValueFromTemplate?.errors || res?.errors?.map(item => item?.message))
				return
			}
			this.fetchTemplate()
		},

		setErrors(errors = [], { hint = "" } = {}) {
			this.errors = errors?.map(err => ({ message: err, hint }))
		},

		createTable() {
			const tableSchema = { hello: "World!" }

			this.addDynamicValue({ valueJsonString: JSON.stringify(tableSchema) })
		},

		handleUpdateTable(value) {
			this.template.dynamicValues[0].valueJsonString = value
		}
	},

	async created() {
		await this.fetchTemplate()
		this.selectedId = this.template?.dynamicValues?.[0]?.valueId ?? ""
	},

	watch: {
		computedSelectedValue: {
			handler() {
				this.valueJson = this.computedSelectedValue?.valueJsonString ?? ""
			},
			immediate: true,
			deep: true
		}
	},

	beforeRouteLeave(to, from, next) {
		preventBrowserBackButton({ next, hasChanged: this.hasChanged })
	}
}
</script>
